import { GitMergeIcon, GitPullRequestIcon } from '@phosphor-icons/react';

import type { PullRequestState } from '@/api-models/sandbox-git';

/** Icon per PR state, matching the Review tab's badge vocabulary so the two never disagree. */
export const PR_MARK: Record<PullRequestState, { icon: typeof GitPullRequestIcon; className: string }> = {
  open: { icon: GitPullRequestIcon, className: 'text-diff-add' },
  draft: { icon: GitPullRequestIcon, className: 'text-fg-tertiary' },
  merged: { icon: GitMergeIcon, className: 'text-success' },
  closed: { icon: GitPullRequestIcon, className: 'text-diff-del' },
};

const STATE_LABEL: Record<PullRequestState, string> = {
  open: 'Open',
  draft: 'Draft',
  merged: 'Merged',
  closed: 'Closed',
};

/** The fields every PR shape in the app shares — the per-session lookup and the team-wide one. */
export interface PullRequestChipData {
  number: number;
  state: PullRequestState;
  title: string;
  html_url: string;
}

/**
 * A session's pull request as a header chip: state glyph plus number, linking out to the provider.
 *
 * Shaped like the repo and branch chips beside it, so the session header reads as one row of
 * facts about the session. The state lives in the glyph's colour *and* the tooltip — never colour
 * alone — because the number is what the chip is for and a second word would crowd the bar.
 */
export function PullRequestChip({ pull }: { pull: PullRequestChipData }) {
  const mark = PR_MARK[pull.state];
  return (
    <a
      href={pull.html_url}
      target="_blank"
      rel="noreferrer"
      title={`${pull.title} — #${pull.number} (${STATE_LABEL[pull.state]})`}
      className="flex shrink-0 items-center gap-1 rounded-md border border-line bg-surface-3 px-2 py-1 text-2xs tabular-nums text-fg-secondary no-underline outline-none transition-colors hover:border-line-strong hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
    >
      <mark.icon size={12} weight="bold" className={mark.className} />#{pull.number}
    </a>
  );
}
